import { Children, type ReactNode } from "react"

import InfoTooltip from "~/components/reusable/InfoTooltip"
import { withTypographicMinus } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import { figureToneClass, type FigureTone } from "~/utils/figureTone"

/**
 * Page-level figure: label (+ optional info tooltip), value, optional delta + caption. `tone`
 * colours the delta only, unless `valueIsDifference` moves it onto the value.
 * Canonical intent: {@link ../design-system/intents.ts STAT_TILE_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const StatTile = ({
  label,
  info,
  value,
  delta,
  caption,
  tone = "neutral",
  valueIsDifference = false,
  className,
}: {
  label: string
  info?: string
  value: ReactNode
  delta?: ReactNode
  caption?: ReactNode
  tone?: FigureTone
  /** The value is itself the difference, so `tone` colours it instead of the delta. */
  valueIsDifference?: boolean
  className?: string
}) => {
  const valueTone = valueIsDifference ? figureToneClass(tone, "text-fg-base") : "text-fg-base"
  const deltaTone = valueIsDifference ? "text-fg-muted" : figureToneClass(tone)
  return (
    <div className={cn("flex min-w-0 flex-col gap-1 px-4 py-3", className)}>
      {/* Inline, so the icon follows the last word when a long label wraps. */}
      <span className="label-sm text-fg-subtle">
        {label}
        {info && (
          <InfoTooltip title={info} className="ml-1 inline-block size-3.5 shrink-0 align-[-2px]" />
        )}
      </span>
      {/* Never truncated: a figure is compared, so it keeps its room. */}
      <span className={cn("header-h2 tabular-nums whitespace-nowrap", valueTone)}>
        {withTypographicMinus(value)}
      </span>
      {(delta != null || caption != null) && (
        <span className="paragraph-sm flex flex-wrap gap-x-1">
          {delta != null && (
            <span className={cn("tabular-nums", deltaTone)}>{withTypographicMinus(delta)}</span>
          )}
          {caption != null && <span className="text-fg-muted">{caption}</span>}
        </span>
      )}
    </div>
  )
}

/**
 * {@link StatTile}s sharing the row equally, split by 1px dividers. Wraps by its own width: one row
 * from 720px, two per row from 360px, stacked below; a tile never drops under 180px. Pass
 * `className="border-b border-base"` when content follows it.
 */
// Six tiles go 6-up, then 3 + 3, then 2-up: a first row of 5 or 4 would leave an orphan below it.
const SIX_UP = "@[360px]:[&>*]:basis-1/2 @[540px]:[&>*]:basis-1/3 @[1080px]:[&>*]:basis-1/6"
// Any other count: one row from 720px, 2-up from 360px.
const ONE_ROW = "[&>*]:min-w-[180px] @[360px]:[&>*]:basis-1/2 @[720px]:[&>*]:basis-0"

export const StatTileRow = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => (
  // Every tile draws its left and top border; the -1px offset and overflow-hidden clip the ones on
  // the outer edges, so dividers stay correct however the tiles wrap.
  <div className={cn("@container overflow-hidden", className)}>
    <div
      className={cn(
        "-ml-px -mt-px flex flex-wrap [&>*]:grow [&>*]:basis-full [&>*]:border-l [&>*]:border-t [&>*]:border-base",
        Children.toArray(children).length === 6 ? SIX_UP : ONE_ROW,
      )}
    >
      {children}
    </div>
  </div>
)
